import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faThumbsUp, faThumbsDown, faListUl } from '@fortawesome/free-solid-svg-icons';

import { formatMoney, formatPlate } from '../garage/format';
import { useProvider } from './ProviderContext';
import { loadEvaluationItems } from './providerData';
import { formatEvaluationDate } from './providerFormat';
import Stars from './Stars';

// Detalhe de uma avaliacao: folha que sobe no celular, gaveta lateral no desktop (rs-*).
export default function EvaluationSheet({ evaluation, onClose }) {
  const { company } = useProvider();
  const [items, setItems] = useState(null);
  const [itemsFailed, setItemsFailed] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    let active = true;
    loadEvaluationItems(company, evaluation)
      .then((list) => { if (active) setItems(list); })
      .catch(() => { if (active) setItemsFailed(true); });
    return () => { active = false; };
  }, [company, evaluation]);

  useEffect(() => {
    closeRef.current && closeRef.current.focus();
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  const facts = [
    evaluation.type && { label: 'Tipo', value: evaluation.type },
    evaluation.code && { label: 'Número', value: evaluation.code },
    evaluation.plate && { label: 'Placa', value: formatPlate(evaluation.plate) },
    evaluation.value !== null && { label: 'Valor', value: formatMoney(evaluation.value) },
  ].filter(Boolean);

  return createPortal(
    <div className='rs-overlay' onClick={onClose}>
      <div className='rs-sheet' role='dialog' aria-modal='true' aria-labelledby='pv-sheet-title' onClick={(event) => event.stopPropagation()}>
        <header className='rs-header'>
          <span className='rs-grabber' aria-hidden='true' />
          <div>
            <span className='rs-date'>{formatEvaluationDate(evaluation.date)}</span>
            <h2 id='pv-sheet-title'>{evaluation.client}</h2>
          </div>
          <button type='button' ref={closeRef} className='rs-close' onClick={onClose} aria-label='Fechar avaliação'>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </header>

        <div className='rs-body'>
          <section className='rs-block'>
            <h3>Avaliação</h3>
            <p className={`pv-indicate ${evaluation.indicate ? 'is-yes' : 'is-no'}`}>
              <FontAwesomeIcon icon={evaluation.indicate ? faThumbsUp : faThumbsDown} aria-hidden='true' />
              {evaluation.indicate ? 'Indicaria a sua oficina' : 'Não indicaria a sua oficina'}
            </p>
            <dl className='pv-ratings'>
              <div><dt>Produto ou serviço</dt><dd><Stars value={evaluation.ratingProduct} /></dd></div>
              <div><dt>Preço</dt><dd><Stars value={evaluation.ratingPrice} /></dd></div>
            </dl>
            {evaluation.comment && <blockquote className='pv-quote'>{evaluation.comment}</blockquote>}
          </section>

          {facts.length > 0 && (
            <section className='rs-block'>
              <h3>Atendimento</h3>
              <dl className='rs-facts'>
                {facts.map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}
              </dl>
            </section>
          )}

          <section className='rs-block'>
            <h3><FontAwesomeIcon icon={faListUl} aria-hidden='true' />Itens</h3>
            {!items && !itemsFailed && <div className='gs-skeleton gs-skeleton-row' aria-busy='true' />}
            {itemsFailed && <p className='gs-notice'>Não foi possível carregar os itens deste atendimento.</p>}
            {items && items.length === 0 && <p className='gs-muted'>O atendimento não tem itens registrados.</p>}
            {items && items.length > 0 && (
              <ul className='rs-items'>
                {items.map((item, index) => (
                  <li key={`${item.description}-${index}`} className='rs-item'>
                    <div className='rs-item-main'>
                      <strong>{item.description}</strong>
                      <span>{item.quantity} × {formatMoney(item.unitValue)}</span>
                    </div>
                    <span className='rs-item-total'>{formatMoney(item.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}
